import { EncounterPhase } from 'app/frontend/types/custom/EncounterEvents'
import {
  RaidPlanCast,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import {
  buildPhaseStartsInOrder,
  resolvePhaseIndexAtTime as resolvePhaseIndexAtTimeFor,
} from './phaseTiming'

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const cloneTimeline = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

const toPhaseDurationSeconds = (duration: number) =>
  Math.max(Math.round(duration), 0)

type PhaseBoundaryHandleLike = {
  id: string
  index: number
  min: number
  max: number
}

export type PhaseDragBaseline = {
  timeline: RaidPlanTimeline
  phaseOrder: number[]
  phaseStarts: number[]
  phaseEnds: number[]
  durations: number[]
  phaseIdByIndex: Record<number, string>
  phaseIndexById: Record<string, number>
  handleByIndex: Record<number, { min: number; max: number }>
}

export const buildPhaseDragBaseline = ({
  timeline,
  phaseOrder,
  phaseStarts,
  phaseEnds,
  durations,
  handles,
}: {
  timeline: RaidPlanTimeline
  phaseOrder: number[]
  phaseStarts: number[]
  phaseEnds: number[]
  durations: number[]
  handles: PhaseBoundaryHandleLike[]
}): PhaseDragBaseline => {
  const phaseIdByIndex: Record<number, string> = {}
  const phaseIndexById: Record<string, number> = {}
  const handleByIndex: Record<number, { min: number; max: number }> = {}
  handles.forEach((handle) => {
    phaseIdByIndex[handle.index] = handle.id
    phaseIndexById[handle.id] = handle.index
    handleByIndex[handle.index] = { min: handle.min, max: handle.max }
  })
  return {
    timeline: cloneTimeline(timeline),
    phaseOrder: [...phaseOrder],
    phaseStarts: [...phaseStarts],
    phaseEnds: [...phaseEnds],
    durations: [...durations],
    phaseIdByIndex,
    phaseIndexById,
    handleByIndex,
  }
}

export const applyPhaseBoundaryChange = (
  baseline: PhaseDragBaseline,
  boundaryIndex: number,
  nextAt: number,
): RaidPlanTimeline | null => {
  const draggedHandle = baseline.handleByIndex[boundaryIndex]
  const draggedPhaseId = baseline.phaseIdByIndex[boundaryIndex]
  if (!draggedHandle || draggedPhaseId === undefined) return null

  const baselineStart = baseline.phaseStarts[boundaryIndex] ?? 0
  const clampedTarget = Math.floor(
    clamp(nextAt, draggedHandle.min, draggedHandle.max),
  )
  const newDuration = Math.max(clampedTarget - baselineStart, 0)

  const newDurations = [...baseline.durations]
  newDurations[boundaryIndex] = newDuration
  const newStarts = buildPhaseStartsInOrder(newDurations, baseline.phaseOrder)
  const deltas = baseline.phaseStarts.map(
    (start, index) => (newStarts[index] ?? 0) - start,
  )

  const resolveBaselinePhaseIndex = (at: number) =>
    resolvePhaseIndexAtTimeFor(
      at,
      baseline.phaseStarts,
      baseline.phaseEnds,
      baseline.phaseOrder,
    )

  const shiftCast = (cast: RaidPlanCast) => {
    const baseAt = cast.at
    const boundPhaseIndex =
      cast.hidden &&
      cast.hidden_phase_id !== undefined &&
      baseline.phaseIndexById[cast.hidden_phase_id] !== undefined
        ? baseline.phaseIndexById[cast.hidden_phase_id]
        : resolveBaselinePhaseIndex(baseAt)
    const phaseStart = baseline.phaseStarts[boundPhaseIndex] ?? 0
    const offset = baseAt - phaseStart
    const phaseDuration = newDurations[boundPhaseIndex] ?? 0
    cast.at = Math.floor(baseAt + (deltas[boundPhaseIndex] ?? 0))
    if (offset >= phaseDuration) {
      cast.hidden = true
      cast.hidden_phase_id = baseline.phaseIdByIndex[boundPhaseIndex]
    } else {
      delete cast.hidden
      delete cast.hidden_phase_id
    }
  }

  const transformed = cloneTimeline(baseline.timeline)
  transformed.actors.forEach((actor) => actor.casts.forEach(shiftCast))
  transformed.general?.casts.forEach(shiftCast)
  Object.values(transformed.encounter_assignments?.abilities ?? {}).forEach(
    (ability) => {
      Object.values(ability.cast_overrides ?? {}).forEach((override) => {
        if (typeof override.time !== 'number') return
        const phaseIndex =
          typeof override.phase_index === 'number'
            ? override.phase_index
            : resolveBaselinePhaseIndex(override.time)
        override.time = Math.floor(override.time + (deltas[phaseIndex] ?? 0))
      })
    },
  )
  transformed.phase_durations = {
    ...(transformed.phase_durations ?? {}),
    [draggedPhaseId]: newDuration,
  }

  return transformed
}

type PhaseDurationDifficulty = {
  difficulty: string
  phases: EncounterPhase[]
}

export const selectPhaseTimingSource = <
  T extends { difficulty: string; phases: readonly unknown[] },
>(
  difficulties: readonly T[],
  primaryDifficulty: string,
): T | undefined => {
  const primary =
    difficulties.find((entry) => entry.difficulty === primaryDifficulty) ??
    difficulties[0]
  if (!primary) return undefined
  return difficulties.reduce(
    (best, difficulty) =>
      difficulty.phases.length > best.phases.length ? difficulty : best,
    primary,
  )
}

export const canonicalPhaseDurations = (
  phaseDurations?: Record<string, Record<string, number>> | null,
): Record<string, Record<string, number>> => {
  const result: Record<string, Record<string, number>> = {}
  Object.keys(phaseDurations ?? {})
    .sort()
    .forEach((difficulty) => {
      const phases = phaseDurations?.[difficulty] ?? {}
      const bucket: Record<string, number> = {}
      Object.keys(phases)
        .sort()
        .forEach((phaseId) => {
          const duration = phases[phaseId]
          if (typeof duration !== 'number') return
          bucket[phaseId] = Math.max(Math.round(duration), 0)
        })
      if (Object.keys(bucket).length > 0) result[difficulty] = bucket
    })
  return result
}

// The editor shows one difficulty's phases. A shortened phase is copied onto
// the same index of every difficulty, and never past that difficulty's own
// static length. A phase left at its static length is omitted.
export const mirrorShortenedPhaseDurations = ({
  sourcePhases,
  sourcePhaseDurations,
  difficulties,
}: {
  sourcePhases: EncounterPhase[]
  sourcePhaseDurations?: Record<string, number> | null
  difficulties: PhaseDurationDifficulty[]
}): Record<string, Record<string, number>> => {
  const persisted = sourcePhaseDurations ?? {}
  const result: Record<string, Record<string, number>> = {}

  sourcePhases.forEach((sourcePhase, index) => {
    const staticDuration = toPhaseDurationSeconds(sourcePhase.duration)
    const persistedDuration = persisted[sourcePhase.id]
    const effective =
      typeof persistedDuration === 'number'
        ? Math.max(Math.round(persistedDuration), 0)
        : staticDuration
    if (effective >= staticDuration) return

    difficulties.forEach((difficulty) => {
      const phase = difficulty.phases[index]
      if (!phase) return
      const difficultyStatic = toPhaseDurationSeconds(phase.duration)
      const stored = Math.min(effective, difficultyStatic)
      if (stored >= difficultyStatic) return
      const bucket = (result[difficulty.difficulty] ??= {})
      bucket[phase.id] = stored
    })
  })

  return result
}

export const phaseDurationsForTimingSource = ({
  difficulties,
  primaryDifficulty,
  phaseDurations,
}: {
  difficulties: PhaseDurationDifficulty[]
  primaryDifficulty: string
  phaseDurations?: Record<string, Record<string, number>> | null
}): Record<string, number> => {
  const source = selectPhaseTimingSource(difficulties, primaryDifficulty)
  if (!source) return {}
  const bucket = phaseDurations?.[source.difficulty]
  if (!bucket) return {}
  return { ...bucket }
}
